import React, { useState } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import {
  useAdmissionReviews,
  useAddAdmissionReview,
  type AdmissionReview,
} from "../../apis/patients";

type Props = {
  admissionId: string;
};

const fmt = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function AdmissionReviewsPanel({ admissionId }: Props) {
  const {
    data: reviews = [],
    isLoading,
    isError,
  } = useAdmissionReviews(admissionId);

  const { mutate: addReview, isPending: isSaving } =
    useAddAdmissionReview(admissionId);

  const [body, setBody] = useState("");

  const handleAdd = () => {
    const trimmed = body.trim();
    if (!trimmed) return;

    addReview(
      {
        body: trimmed,
        authorRole: "doctor",
      },
      {
        onSuccess: () => {
          setBody("");
        },
      }
    );
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
      {/* Existing reviews */}
      {isLoading ? (
        <Spinner size="sm" />
      ) : isError ? (
        <Text fontSize="sm" color="red.500">
          Failed to load doctor reviews.
        </Text>
      ) : !reviews || reviews.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No doctor reviews recorded yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3} mb={3}>
          {reviews.map((r: AdmissionReview) => (
            <Box key={r._id} p={2} borderWidth="1px" borderRadius="md">
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Badge colorScheme="blue">
                    {(r.authorRole || "doctor").toUpperCase()}
                  </Badge>
                  {r.authorName && (
                    <Text fontSize="xs" color="gray.700">
                      {r.authorName}
                    </Text>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {fmt(r.createdAt)}
                </Text>
              </HStack>
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {r.body}
              </Text>
            </Box>
          ))}
        </VStack>
      )}

      {/* Add new doctor review */}
      <Box mt={3}>
        <Textarea
          placeholder="Write a doctor review / ward round note..."
          size="sm"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <HStack mt={2} justify="flex-end">
          <Button
            size="sm"
            colorScheme="purple"
            onClick={handleAdd}
            isLoading={isSaving}
            isDisabled={isSaving || !body.trim()}
          >
            Add Review
          </Button>
        </HStack>
      </Box>
    </Box>
  );
}
